import React, { useCallback, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { allCountries } from 'country-telephone-data'
import Dropdown from 'components/Luxkit/Dropdown/v2/Dropdown'
import Flag from 'components/Luxkit/Flag'
import { getPrefixCountry } from 'lib/geo/getPrefixCountry'
import noop from 'lib/function/noop'
import PhonePrefixItem from './PhonePrefixItem'
import TextInputOnly from '../TextInputOnly'
import InputLabelWrap from '../InputLabelWrap'
import config from 'constants/config'
import useToggle from 'hooks/useToggle'
import DropdownContainer from 'components/Luxkit/Dropdown/DropdownContainer'
import BodyText from 'components/Luxkit/Typography/BodyText'

const PrefixDropdownContainer = styled(DropdownContainer)`
  min-width: ${rem(160)};
  max-height: ${rem(500)};
`

const PhoneWrapper = styled.span`
  width: 100%;
  position: relative;
  display: flex;
`

const NumberInput = styled(TextInputOnly)`
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  flex: 1;
  `

const PrefixDropdown = styled(TextInputOnly)`
  border-right-color: transparent;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  max-width: ${rem(120)};
`

export interface PhonePrefixCountry {
  dialCode: string;
  format: string;
  iso2: string;
  name: string;
  priority: number;
}

interface Props {
  defaultPhoneNumber?: string;
  defaultPhonePrefix?: string;
  phoneNumber?: string;
  onNumberChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPrefixChange?: (data: { phonePrefix: string, code: string}) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  prefixCountry?: string;
  phonePrefix?: string;
  placeholder?: string;
  inputRef?: any;
  name?: string;
  required?: boolean;
  requiredErrorMessage?: string;
  inputTestId?: string;
  id?: string;
  noValidationSpacing?: boolean;
  label?: string;
  className?: string;
  autoComplete?: string;
  phonePrefixOptions?: PhonePrefixCountry[];
  disabled?: boolean;
  /**
   * By default the name will assume a nested object.
   * In VERY RARE circumstances you might need to change this, this prop allows
   * changing the prefix name to a flat string
   */
  prefixNameOverride?: string;
    /**
   * By default the name will assume a nested object.
   * In VERY RARE circumstances you might need to change this, this prop allows
   * changing the phone name to a flat string
   */
  phoneNameOverride?: string;
}

function PhoneInput(props: Props) {
  const {
    autoComplete,
    defaultPhoneNumber,
    defaultPhonePrefix,
    phoneNumber,
    onBlur,
    prefixCountry,
    phonePrefix,
    placeholder = 'Phone',
    inputRef,
    required,
    requiredErrorMessage,
    name,
    onNumberChange = noop,
    inputTestId,
    id,
    onPrefixChange = noop,
    label,
    noValidationSpacing,
    className,
    phonePrefixOptions,
    disabled,
    prefixNameOverride,
    phoneNameOverride,
  } = props

  const prefixTriggerRef = useRef<HTMLDivElement>(null)
  const { value: isPrefixDropdownOpen, on: openPrefixDropdown, off: closePrefixDropdown } = useToggle()
  const [localPrefix, setLocalPrefix] = useState<string | undefined>(phonePrefix ?? defaultPhonePrefix)
  const [localCountryCode, setLocalCountryCode] = useState<string | undefined>(prefixCountry)
  const [prefixTypeahead, setPrefixTypeahead] = useState<string | undefined>()

  const onPrefixSelect = useCallback((country: PhonePrefixCountry) => {
    setLocalPrefix(country.dialCode)
    setLocalCountryCode(country.iso2.toUpperCase())
    setPrefixTypeahead(undefined)
    onPrefixChange({
      phonePrefix: country.dialCode,
      code: country.iso2.toUpperCase(),
    })
    closePrefixDropdown()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onPrefixChange])

  const allowedCountries = useMemo<PhonePrefixCountry[]>(() => {
    if (phonePrefixOptions) {
      return phonePrefixOptions
    }
    if (config.restrictPhoneNumbersToSupportedRegions) {
      const allowedCodes = new Set(config.regions.map(region => region.code))
      return allCountries.filter((country: PhonePrefixCountry) => (allowedCodes.has(country.iso2.toUpperCase())))
    }
    return allCountries
  }, [phonePrefixOptions])

  const country = useMemo(() => getPrefixCountry(localCountryCode || prefixCountry, phonePrefix || localPrefix), [localCountryCode, localPrefix, phonePrefix, prefixCountry])

  const hiddenPrefixes = useMemo(() => {
    if (prefixTypeahead) {
      const hiddenCountries = allowedCountries.filter(option =>
        !option.name.toLowerCase().includes(prefixTypeahead.toLowerCase()) &&
        !option.dialCode.includes(prefixTypeahead),
      )
      return new Set(hiddenCountries)
    }
    return new Set()
  }, [allowedCountries, prefixTypeahead])

  const onPrefixDropdownClose = useCallback(() => {
    setPrefixTypeahead(undefined)
    closePrefixDropdown()
  }, [closePrefixDropdown])

  return (
    <InputLabelWrap
      label={label}
      id={id}
      className={className}
      required={required}
  >
      <PhoneWrapper>
        <input type="hidden" name={prefixNameOverride ?? `${name}.prefix`} value={country.phonePrefix} />
        <PrefixDropdown
          autoComplete="off"
          noValidationSpacing={noValidationSpacing}
          startIcon={<Flag data-testid={`flag-${country?.prefixCountry}`} countryCode={country?.prefixCountry}/>}
          disabled={disabled}
          onClick={openPrefixDropdown}
          containerRef={prefixTriggerRef}
          value={prefixTypeahead ?? `+${country.phonePrefix}`}
          onFocus={() => {
            openPrefixDropdown()
            setPrefixTypeahead('')
          }}
          onChange={(e) => setPrefixTypeahead(e.currentTarget.value)}
        />
        <NumberInput
          disabled={disabled}
          maxLength={15}
          minLength={7}
          id={id}
          type="tel"
          placeholder={placeholder}
          name={phoneNameOverride ?? `${name}.phone`}
          onChange={onNumberChange}
          onBlur={onBlur}
          defaultValue={defaultPhoneNumber}
          value={phoneNumber}
          ref={inputRef}
          required={required}
          requiredErrorMessage={requiredErrorMessage}
          data-testid={inputTestId}
          noValidationSpacing={noValidationSpacing}
          pattern="^[0-9\s]*$"
          autoComplete={autoComplete}
        />
        <Dropdown
          open={isPrefixDropdownOpen}
          onClose={onPrefixDropdownClose}
          triggerRef={prefixTriggerRef}
        >
          <PrefixDropdownContainer fit="dropdown-items">
            {hiddenPrefixes.size === allowedCountries.length && <BodyText variant="medium">No matching dial codes</BodyText>}
            {allowedCountries.map(country => (
              <PhonePrefixItem
                key={country.iso2}
                selected={localCountryCode === country.iso2.toLocaleUpperCase()}
                country={country}
                onSelect={onPrefixSelect}
                hidden={hiddenPrefixes.has(country)}
              />
            ))}
          </PrefixDropdownContainer>
        </Dropdown>
      </PhoneWrapper>
    </InputLabelWrap>
  )
}

export default PhoneInput
